// =====================================================
// COMPOSANT: RateDelta — formats de taux + badge Δ N-1 en POINTS (RUN 3)
// =====================================================
// Section « Conversion & traçabilité commerciale ». Un taux se compare en points
// de pourcentage (pas en %), et sa couleur dépend du SENS métier : pour une
// conversion, monter est bon (vert) ; pour un taux d'annulation, monter est
// mauvais (rouge). `goodDirection` porte cette sémantique. Design system crm-4l.

import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { RateValue } from '@/lib/dashboard-stats';
import { fmtRate } from './rate-format';

interface RateDeltaProps {
  rate: RateValue;
  /** Sens « favorable » de la variation : 'up' (conversion) ou 'down' (annulation). */
  goodDirection: 'up' | 'down';
  className?: string;
}

/** Badge Δ N-1 en points de pourcentage, coloré selon le sens métier. */
export function RateDelta({ rate, goodDirection, className = '' }: RateDeltaProps) {
  if (rate.n1Unavailable || rate.deltaPts == null) {
    return (
      <span
        className={`inline-flex w-fit items-center gap-1 rounded-full border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs font-medium text-zinc-500 ${className}`}
      >
        <Minus className="h-3 w-3" />
        N/A vs N-1
      </span>
    );
  }

  const pts = rate.deltaPts;
  const rising = pts > 0;
  const flat = Math.abs(pts) < 0.05;
  // Favorable = variation dans le bon sens métier.
  const favorable = flat ? true : goodDirection === 'up' ? rising : !rising;
  const Icon = flat ? Minus : rising ? TrendingUp : TrendingDown;
  const sign = pts > 0 ? '+' : pts < 0 ? '−' : '';
  const abs = Math.abs(pts).toLocaleString('fr-FR', { maximumFractionDigits: 1 });
  const unit = Math.abs(pts) >= 2 ? 'pts' : 'pt';

  return (
    <span
      className={[
        'inline-flex w-fit items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold tabular-nums',
        favorable
          ? 'border-green-200 bg-green-50 text-green-700'
          : 'border-red-200 bg-red-50 text-red-700',
        className,
      ].join(' ')}
    >
      <Icon className="h-3 w-3" />
      {sign}
      {abs} {unit}
      <span className="font-normal text-zinc-500">(vs {fmtRate(rate.prevValue)} N-1)</span>
    </span>
  );
}
